import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useChainsTheme } from '../../context/ChainsThemeContext';
import { 
  BarChart3, TrendingUp, Package, Truck, Warehouse, 
  Globe, MapPin, Activity, Clock, AlertTriangle,
  CheckCircle, XCircle, ArrowUpRight, ArrowDownRight,
  Plus, Filter, Download, RefreshCw, Eye, Settings,
  Users, DollarSign, ShoppingCart, FileText
} from 'lucide-react';
import { 
  LineChart, Line, AreaChart, Area, BarChart, Bar, 
  PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer, ScatterChart, 
  Scatter, ComposedChart, RadarChart, PolarGrid, 
  PolarAngleAxis, PolarRadiusAxis, Radar
} from 'recharts';

const ChainsDashboard = () => {
  const [activeView, setActiveView] = useState('overview');
  const [selectedWarehouse, setSelectedWarehouse] = useState('all');
  const [timeRange, setTimeRange] = useState('7d');
  const { isDarkMode } = useChainsTheme();

  // Mock data for impressive visualizations
  const supplyChainData = [
    { month: 'Jan', orders: 1250, deliveries: 1180, returns: 45, efficiency: 94.4, cost: 125000 },
    { month: 'Feb', orders: 1380, deliveries: 1320, returns: 38, efficiency: 95.7, cost: 138000 },
    { month: 'Mar', orders: 1520, deliveries: 1450, returns: 42, efficiency: 95.4, cost: 152000 },
    { month: 'Apr', orders: 1680, deliveries: 1620, returns: 35, efficiency: 96.4, cost: 168000 },
    { month: 'May', orders: 1820, deliveries: 1750, returns: 48, efficiency: 96.2, cost: 182000 },
    { month: 'Jun', orders: 1950, deliveries: 1880, returns: 41, efficiency: 96.4, cost: 195000 },
  ];

  const warehouseHeatmapData = [
    { id: 'WH-001', name: 'Riyadh Central', capacity: 85, utilization: 78, temperature: 22, humidity: 45, alerts: 2 },
    { id: 'WH-002', name: 'Jeddah Port', capacity: 92, utilization: 85, temperature: 24, humidity: 52, alerts: 1 },
    { id: 'WH-003', name: 'Dammam Industrial', capacity: 78, utilization: 72, temperature: 21, humidity: 48, alerts: 0 },
    { id: 'WH-004', name: 'Abha Regional', capacity: 65, utilization: 58, temperature: 19, humidity: 42, alerts: 3 },
    { id: 'WH-005', name: 'Tabuk Storage', capacity: 88, utilization: 81, temperature: 23, humidity: 50, alerts: 1 },
  ];

  const logisticsMapData = [
    { id: 'TR-001', origin: 'Riyadh', destination: 'Jeddah', status: 'in_transit', progress: 65, eta: '2h 30m' },
    { id: 'TR-002', origin: 'Dammam', destination: 'Riyadh', status: 'delivered', progress: 100, eta: 'Completed' },
    { id: 'TR-003', origin: 'Jeddah', destination: 'Abha', status: 'pending', progress: 0, eta: '4h 15m' },
    { id: 'TR-004', origin: 'Riyadh', destination: 'Tabuk', status: 'in_transit', progress: 35, eta: '5h 45m' },
  ];

  const performanceMetrics = [
    { name: 'Order Fulfillment', value: 96.4, target: 95, trend: 'up', change: '+1.4%', icon: CheckCircle, color: 'emerald' },
    { name: 'Delivery Time', value: 2.3, target: 2.5, trend: 'up', change: '-0.2h', icon: Clock, color: 'blue' },
    { name: 'Cost Efficiency', value: 94.2, target: 92, trend: 'up', change: '+2.2%', icon: DollarSign, color: 'green' },
    { name: 'Return Rate', value: 2.1, target: 3.0, trend: 'up', change: '-0.9%', icon: XCircle, color: 'red' },
  ];

  const vendorPerformance = [
    { name: 'TechCorp Solutions', rating: 4.8, orders: 156, onTime: 98, cost: 125000 },
    { name: 'Global Logistics Co.', rating: 4.6, orders: 142, onTime: 95, cost: 118000 },
    { name: 'FastTrack Express', rating: 4.4, orders: 128, onTime: 92, cost: 112000 },
    { name: 'Premium Suppliers', rating: 4.7, orders: 134, onTime: 96, cost: 120000 },
  ];

  const getUtilizationColor = (utilization) => {
    if (utilization >= 90) return 'bg-red-500';
    if (utilization >= 80) return 'bg-orange-500';
    if (utilization >= 70) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getTemperatureColor = (temp) => {
    if (temp > 25) return 'text-red-500';
    if (temp > 22) return 'text-orange-500';
    return 'text-green-500';
  };

  const WarehouseHeatmapCard = ({ warehouse }) => (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ scale: 1.02 }}
      className={`backdrop-blur-sm rounded-2xl p-6 hover:shadow-xl transition-all duration-300 ${
        isDarkMode 
          ? 'bg-white/10 border border-white/20' 
          : 'bg-white/70 border border-gray-200/50'
      }`}
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{warehouse.name}</h3>
          <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>{warehouse.id}</p>
        </div>
        <div className={`w-4 h-4 rounded-full ${getUtilizationColor(warehouse.utilization)}`}></div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="text-center">
          <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Capacity</p>
          <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{warehouse.capacity}%</p>
        </div>
        <div className="text-center">
          <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Utilization</p>
          <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{warehouse.utilization}%</p>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <span className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Temperature</span>
          <span className={`font-medium ${getTemperatureColor(warehouse.temperature)}`}>
            {warehouse.temperature}°C
          </span>
        </div>
        <div className="flex justify-between items-center">
          <span className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Humidity</span>
          <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{warehouse.humidity}%</span>
        </div>
        <div className="flex justify-between items-center">
          <span className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Alerts</span>
          <span className={`font-medium ${warehouse.alerts > 0 ? 'text-red-400' : 'text-green-400'}`}>
            {warehouse.alerts}
          </span>
        </div>
      </div>

      {/* Heatmap Visualization */}
      <div className="mt-4">
        <div className="flex justify-between text-xs mb-2">
          <span className={isDarkMode ? 'text-white/60' : 'text-gray-600'}>Storage Zones</span>
          <span className={isDarkMode ? 'text-white/60' : 'text-gray-600'}>{warehouse.utilization}% Full</span>
        </div>
        <div className="grid grid-cols-8 gap-1">
          {[...Array(64)].map((_, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: i * 0.01 }}
              className={`h-3 rounded-sm ${
                i < (warehouse.utilization / 100) * 64
                  ? getUtilizationColor(warehouse.utilization)
                  : isDarkMode ? 'bg-white/10' : 'bg-gray-200'
              }`}
            />
          ))}
        </div>
      </div>
    </motion.div>
  );

  const LogisticsMapCard = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`backdrop-blur-sm rounded-2xl p-6 ${
        isDarkMode 
          ? 'bg-white/10 border border-white/20' 
          : 'bg-white/70 border border-gray-200/50'
      }`}
    >
      <div className="flex items-center justify-between mb-6">
        <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Live Logistics Map</h3>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
          <span className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Live</span>
        </div>
      </div>

      {/* Simplified Map Visualization */}
      <div className={`relative h-64 rounded-xl border overflow-hidden ${
        isDarkMode 
          ? 'bg-gradient-to-br from-blue-900/50 to-slate-900/50 border-white/20' 
          : 'bg-gradient-to-br from-blue-50 to-slate-100 border-gray-200/50'
      }`}>
        {/* Map Background */}
        <div className={`absolute inset-0 ${
          isDarkMode 
            ? 'bg-[radial-gradient(circle_at_30%_30%,rgba(59,130,246,0.3),transparent)]' 
            : 'bg-[radial-gradient(circle_at_30%_30%,rgba(59,130,246,0.1),transparent)]'
        }`}></div>
        
        {/* City Markers */}
        {['Riyadh', 'Jeddah', 'Dammam', 'Abha', 'Tabuk'].map((city, index) => (
          <motion.div
            key={city}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: index * 0.2 }}
            className="absolute"
            style={{
              left: `${20 + (index * 15)}%`,
              top: `${30 + (index * 10)}%`,
            }}
          >
            <div className={`w-4 h-4 rounded-full shadow-lg ${isDarkMode ? 'bg-white' : 'bg-gray-800'}`}></div>
            <p className={`text-xs mt-1 text-center ${isDarkMode ? 'text-white/80' : 'text-gray-700'}`}>{city}</p>
          </motion.div>
        ))}

        {/* Moving Trucks */}
        {logisticsMapData.filter(item => item.status === 'in_transit').map((truck, index) => (
          <motion.div
            key={truck.id}
            animate={{
              x: [0, 200, 400],
              y: [0, -50, 0],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "linear",
              delay: index * 2
            }}
            className="absolute w-6 h-6 bg-orange-500 rounded-full shadow-lg flex items-center justify-center"
          >
            <Truck className="w-3 h-3 text-white" />
          </motion.div>
        ))}
      </div>

      {/* Shipment List */}
      <div className="mt-4 space-y-2">
        {logisticsMapData.map((shipment) => (
          <div key={shipment.id} className={`flex items-center justify-between p-3 rounded-lg ${
            isDarkMode ? 'bg-white/5' : 'bg-gray-50'
          }`}>
            <div>
              <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{shipment.id}</p>
              <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>{shipment.origin} → {shipment.destination}</p>
            </div>
            <div className="text-right">
              <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>{shipment.eta}</p>
              <div className={`w-16 h-1 rounded-full overflow-hidden ${
                isDarkMode ? 'bg-white/20' : 'bg-gray-200'
              }`}>
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${shipment.progress}%` }}
                  className={`h-full ${shipment.progress >= 80 ? 'bg-green-500' : 'bg-blue-500'}`}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );

  return (
    <div className="p-6 space-y-8 h-full overflow-y-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div>
          <h1 className={`text-4xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Supply Chain Intelligence</h1>
          <p className={`text-lg ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Real-time insights and predictive analytics</p>
        </div>
        <div className="flex items-center space-x-4">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className={`px-6 py-3 backdrop-blur-sm border rounded-xl transition-all ${
              isDarkMode 
                ? 'bg-white/10 border-white/20 text-white hover:bg-white/20'
                : 'bg-white/50 border-gray-200/50 text-gray-700 hover:bg-white/70'
            }`}
          >
            <RefreshCw className="w-5 h-5 mr-2 inline" />
            Refresh Data
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-600 text-white rounded-xl hover:shadow-lg transition-all"
          >
            <Plus className="w-5 h-5 mr-2 inline" />
            New Order
          </motion.button>
        </div>
      </motion.div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {performanceMetrics.map((metric, index) => (
          <motion.div
            key={metric.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className={`backdrop-blur-sm rounded-2xl p-6 hover:shadow-xl transition-all duration-300 ${
              isDarkMode 
                ? 'bg-white/10 border border-white/20' 
                : 'bg-white/70 border border-gray-200/50'
            }`}
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`p-3 rounded-xl bg-${metric.color}-500/20`}>
                <metric.icon className={`w-6 h-6 text-${metric.color}-400`} />
              </div>
              <div className={`flex items-center text-sm ${
                metric.trend === 'up' ? 'text-green-400' : 'text-red-400'
              }`}>
                {metric.trend === 'up' ? (
                  <ArrowUpRight className="w-4 h-4 mr-1" />
                ) : (
                  <ArrowDownRight className="w-4 h-4 mr-1" />
                )}
                {metric.change}
              </div>
            </div>
            <h3 className={`text-sm mb-2 ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>{metric.name}</h3>
            <p className={`text-3xl font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{metric.value}</p>
            <p className={`text-sm ${isDarkMode ? 'text-white/40' : 'text-gray-500'}`}>Target: {metric.target}</p>
          </motion.div>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Supply Chain Performance Chart */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className={`lg:col-span-2 backdrop-blur-sm rounded-2xl p-6 ${
            isDarkMode 
              ? 'bg-white/10 border border-white/20' 
              : 'bg-white/70 border border-gray-200/50'
          }`}
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Supply Chain Performance</h3>
            <div className="flex items-center space-x-4">
              <select className={`rounded-lg px-3 py-1 text-sm ${
                isDarkMode 
                  ? 'bg-white/10 border border-white/20 text-white' 
                  : 'bg-white/50 border border-gray-200 text-gray-900'
              }`}>
                <option>Last 6 Months</option>
                <option>Last Year</option>
                <option>Custom Range</option>
              </select>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={supplyChainData}>
              <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? "rgba(255,255,255,0.1)" : "#f0f0f0"} />
              <XAxis dataKey="month" stroke={isDarkMode ? "rgba(255,255,255,0.6)" : "#6b7280"} />
              <YAxis stroke={isDarkMode ? "rgba(255,255,255,0.6)" : "#6b7280"} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.9)', 
                  border: 'none', 
                  borderRadius: '12px',
                  color: isDarkMode ? 'white' : 'black'
                }}
              />
              <Area 
                type="monotone" 
                dataKey="orders" 
                stackId="1" 
                stroke="#3B82F6" 
                fill="#3B82F6" 
                fillOpacity={0.3}
              />
              <Area 
                type="monotone" 
                dataKey="deliveries" 
                stackId="1" 
                stroke="#10B981" 
                fill="#10B981" 
                fillOpacity={0.3}
              />
              <Line 
                type="monotone" 
                dataKey="efficiency" 
                stroke="#F59E0B" 
                strokeWidth={3}
                dot={{ fill: '#F59E0B', strokeWidth: 2, r: 4 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Logistics Map */}
        <LogisticsMapCard />
      </div>

      {/* Warehouse Heatmap */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-6"
      >
        <div className="flex items-center justify-between">
          <h3 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Warehouse Intelligence</h3>
          <div className="flex items-center space-x-4">
            <select 
              value={selectedWarehouse}
              onChange={(e) => setSelectedWarehouse(e.target.value)}
              className={`rounded-lg px-4 py-2 ${
                isDarkMode 
                  ? 'bg-white/10 border border-white/20 text-white' 
                  : 'bg-white/50 border border-gray-200 text-gray-900'
              }`}
            >
              <option value="all">All Warehouses</option>
              {warehouseHeatmapData.map(wh => (
                <option key={wh.id} value={wh.id}>{wh.name}</option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
          {warehouseHeatmapData.map((warehouse, index) => (
            <WarehouseHeatmapCard key={warehouse.id} warehouse={warehouse} />
          ))}
        </div>
      </motion.div>

      {/* Vendor Performance */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`backdrop-blur-sm rounded-2xl p-6 ${
          isDarkMode 
            ? 'bg-white/10 border border-white/20' 
            : 'bg-white/70 border border-gray-200/50'
        }`}
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Vendor Performance</h3>
          <button className="text-blue-400 hover:text-blue-300 text-sm">View All Vendors</button>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <RadarChart data={vendorPerformance}>
            <PolarGrid stroke={isDarkMode ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.1)"} />
            <PolarAngleAxis dataKey="name" tick={{ fill: isDarkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }} />
            <PolarRadiusAxis tick={{ fill: isDarkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }} />
            <Radar 
              name="Rating" 
              dataKey="rating" 
              stroke="#3B82F6" 
              fill="#3B82F6" 
              fillOpacity={0.3}
            />
            <Radar 
              name="On-Time %" 
              dataKey="onTime" 
              stroke="#10B981" 
              fill="#10B981" 
              fillOpacity={0.3}
            />
          </RadarChart>
        </ResponsiveContainer>
      </motion.div>
    </div>
  );
};

export default ChainsDashboard; 